import { useNotificationListItems } from '@/components/layout/Navigation/notifications/notification-list-items';
import { NotificationsDisabled } from '@/components/layout/Navigation/notifications/notifications-disabled';
import { NotificationsEmpty } from '@/components/layout/Navigation/notifications/notifications-empty';
import { NotificationsList } from '@/components/layout/Navigation/notifications/notifications-list';
import { NotificationsSkeleton } from '@/components/layout/Navigation/notifications/notifications-skeleton';
import { useOpenComplianceSettings } from '@/components/layout/Navigation/notifications/use-open-compliance-settings';
import { useComplianceNotificationFlagGates } from '@/features/flags/meddevice-compliance-notification-flags';
import {
  PinnedComplianceNotificationsSection,
  useUnreadPinnedComplianceNotifications,
} from '@/features/growth/meddevice-compliance-notifications';
import { complianceNotificationAnalyticsPorts } from '@/models/analytics/features/trackComplianceNotificationAnalytics';

import { useEffectiveSessionTimeout } from '@/features/settings/account/components/security/session-timeout-state';
import { useQueryNotificationPreferences } from '@/hooks/query/useQueryNotificationPreferences';
import { useQueryNotifications } from '@/hooks/query/useQueryNotifications';
import { cn } from '@/lib/cn';
import { NotificationChannel } from '@heidi/schema/ml-scribe';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';

/**
 * Notifications content surface — list / empty / disabled state. Shared by
 * two presentations:
 * - Desktop: rendered inside `NotificationsPopoverContent` (Popover body),
 *   parent height bound by the popover's `h-96`.
 * - Mobile: rendered inside the mobile sheet via `NotificationsPanelContent`
 *   (which adds the flex frame the mobile panel host doesn't supply).
 *
 * Both parents are flex columns with bounded heights — required, or the
 * `flex-1 min-h-0` chain below resolves to 0 and the content disappears.
 *
 * `flex-1 min-h-0` (not `size-full`) so the scroller below resolves to a
 * bounded height inside whichever flex column parent it ends up in.
 * virtua's VList needs a bounded parent; without `min-h-0` the scroller
 * would grow unbounded and VList would measure zero rows on mount.
 */
export const NotificationsPanel = () => {
  const { isPending: isNotificationsPending } = useQueryNotifications();
  const { data: notificationPreferences, isPending: isPreferencesPending } =
    useQueryNotificationPreferences();
  // `useEffectiveSessionTimeout` is not a flag and stayed app-side, so the
  // packaged eligibility hooks take its resolved value as a parameter. Read here
  // and passed down; the mobile sheet renders this same panel, so both
  // presentations get it from one place. `?? null` collapses the hook's
  // `| undefined` arm, which `resolveEffectiveSessionTimeout` declares but
  // cannot return — the package seam takes one absent case, not two.
  const effectiveSessionTimeout = useEffectiveSessionTimeout() ?? null;
  const flagGates = useComplianceNotificationFlagGates();
  // Read once here and handed to the section as a prop: the panel needs the
  // count for the empty/disabled guards below and the section needs the rows,
  // so a second hook call would be a second subscription to the same state.
  const pinnedComplianceNotifications = useUnreadPinnedComplianceNotifications(
    effectiveSessionTimeout,
    flagGates
  );
  const hasPinnedComplianceNotifications = pinnedComplianceNotifications.length > 0;
  const openComplianceSettings = useOpenComplianceSettings();

  const isInmailNotificationEnabled = useMemo(
    () =>
      notificationPreferences?.subscribed_notification_channels.includes(
        NotificationChannel.INMAIL
      ) ?? false,
    [notificationPreferences]
  );

  // Compliance recommendations are not inmails, so the inmail channel
  // preference does not suppress them — only the inmails they sit among.
  const listItems = useNotificationListItems({
    includeInmails: isInmailNotificationEnabled,
  });

  // Both queries are `enabled: !!user (&& !!token)`, so during app boot they
  // stay `pending` until the user query resolves. Without this branch, an
  // undefined `notificationPreferences` falls through to the "!enabled"
  // check and briefly renders `NotificationsDisabled` — which is misleading.
  const isLoading = isNotificationsPending || isPreferencesPending;

  const hasListRows = !isEmpty(listItems);
  const showEmptyState =
    isInmailNotificationEnabled && !hasPinnedComplianceNotifications && !hasListRows;
  const showDisabledState =
    !isInmailNotificationEnabled && !hasPinnedComplianceNotifications && !hasListRows;

  const isCentered = !isLoading && (showEmptyState || showDisabledState);

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-3">
      <div
        aria-busy={isLoading}
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-y-auto',
          isCentered && 'items-center justify-center'
        )}
      >
        {isLoading ? (
          <NotificationsSkeleton />
        ) : (
          <>
            {hasPinnedComplianceNotifications && (
              <PinnedComplianceNotificationsSection
                notifications={pinnedComplianceNotifications}
                onOpenSettings={openComplianceSettings}
                {...complianceNotificationAnalyticsPorts}
              />
            )}
            {hasListRows && <NotificationsList items={listItems} />}
            {showEmptyState && <NotificationsEmpty />}
            {showDisabledState && <NotificationsDisabled />}
          </>
        )}
      </div>
    </div>
  );
};
